import React from 'react';
import { Box, ButtonBase, InputBase } from '@mui/material';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';

export interface NumberFieldProps {
  /** Names the number, shown before it: "Agents  11". */
  label: string;
  value: number;
  /** Called with a number already clamped to `min`–`max`. */
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  /** Shown right before the number, e.g. "$". */
  prefix?: string;
  /** Shown right after the number, e.g. "%". */
  suffix?: string;
  /** `small` for a panel header or small toolbar; `medium` for a page's main inputs. */
  size?: 'small' | 'medium';
  disabled?: boolean;
}

/**
 * A number someone sets — how many agents, a target percentage — in the same
 * box as a `SelectMenu`: the name, then the value, then small up and down
 * buttons.
 *
 * Typing is free while the field has focus; the value is clamped to
 * `min`–`max` and reported as a number, so a half-typed "1" on the way to
 * "15" is not treated as a mistake. Arrow keys step it.
 *
 * Not for a choice from a short list of numbers: use `SegmentedControl`.
 */
export function NumberField({
  label,
  value,
  onChange,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  prefix,
  suffix,
  size = 'small',
  disabled,
}: NumberFieldProps) {
  const id = React.useId();
  const [draft, setDraft] = React.useState(String(value));
  const focused = React.useRef(false);

  // Follow the value from outside, but never overwrite what is being typed.
  React.useEffect(() => {
    if (!focused.current) setDraft(String(value));
  }, [value]);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const commit = (raw: string) => {
    const n = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(n)) return;
    const next = clamp(n);
    if (next !== value) onChange(next);
  };
  // Rounded to the step's own decimals, so 14.5 + 0.1 reads 14.6 rather than
  // the 14.599999999999998 floating point would otherwise show.
  const decimals = (String(step).split('.')[1] ?? '').length;
  const nudge = (dir: 1 | -1) => {
    const raw = (Number.isFinite(Number(draft)) ? Number(draft) : value) + dir * step;
    const next = clamp(Number(raw.toFixed(decimals)));
    setDraft(String(next));
    onChange(next);
  };

  const height = (t: { ds: { sizes: { control: { sm: number; md: number } } } }) =>
    size === 'medium' ? t.ds.sizes.control.md : t.ds.sizes.control.sm;

  return (
    <Box
      sx={(t) => ({
        ...t.typography.body2,
        display: 'inline-flex',
        alignItems: 'center',
        height: height(t),
        pl: size === 'medium' ? 1.5 : 1.25,
        borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.default}`,
        bgcolor: t.tokens.surface.raised,
        opacity: disabled ? 0.6 : 1,
        transition: `border-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
        '&:hover': { borderColor: t.tokens.border.strong },
        '&:focus-within': { borderColor: t.tokens.brand.primary, boxShadow: t.tokens.shadow.focusRing },
      })}
    >
      <Box component="label" htmlFor={id} sx={(t) => ({ color: t.tokens.text.tertiary, mr: 0.75, whiteSpace: 'nowrap' })}>
        {label}
      </Box>
      {prefix && (
        <Box component="span" sx={(t) => ({ fontWeight: 600, color: t.tokens.text.primary })}>
          {prefix}
        </Box>
      )}
      <InputBase
        id={id}
        value={draft}
        disabled={disabled}
        onFocus={() => { focused.current = true; }}
        onBlur={() => {
          focused.current = false;
          commit(draft);
          setDraft(String(value));
        }}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, '');
          setDraft(raw);
          commit(raw);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') { e.preventDefault(); nudge(1); }
          if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1); }
        }}
        inputProps={{
          inputMode: 'decimal',
          'aria-valuemin': min,
          'aria-valuemax': max,
          'aria-valuenow': value,
          role: 'spinbutton',
          size: Math.max(2, draft.length),
        }}
        sx={(t) => ({
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          '& input': { p: 0, textAlign: 'right', width: 'auto', color: t.tokens.text.primary },
        })}
      />
      {suffix && (
        <Box component="span" sx={(t) => ({ fontWeight: 600, color: t.tokens.text.primary, ml: 0.25 })}>
          {suffix}
        </Box>
      )}
      <Box
        sx={(t) => ({
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          ml: 1,
          borderLeft: `1px solid ${t.tokens.border.default}`,
        })}
      >
        {([1, -1] as const).map((dir) => (
          <ButtonBase
            key={dir}
            disabled={disabled || (dir === 1 ? value >= max : value <= min)}
            onClick={() => nudge(dir)}
            aria-label={`${dir === 1 ? 'Increase' : 'Decrease'} ${label.toLowerCase()}`}
            sx={(t) => ({
              flex: 1,
              width: t.spacing(3),
              color: t.tokens.text.tertiary,
              '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
              '&.Mui-disabled': { opacity: 0.4 },
              ...(dir === -1 && { borderTop: `1px solid ${t.tokens.border.default}` }),
              '& svg': { fontSize: t.typography.body2.fontSize },
            })}
          >
            {dir === 1 ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
